import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Menu, X, ChevronDown, Bell,  LogOut, LayoutDashboard, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CATEGORIES, getUser, clearUser, getWishlist } from '@/lib/data';
import NotificationDropdown from './NotificationDropdown';
import SearchOverlay from './SearchOverlay';
import { UserContext } from '@/lib/User';
import axios from 'axios';
import { set } from 'lodash';
// import { log } from 'node:console';

const NOTIF_API = 'https://xceededge.com';

// Framer Motion Variants for Staggered Mobile Menu
const mobileMenuVars = {
  initial: { height: 0, opacity: 0 },
  animate: { 
    height: 'auto', 
    opacity: 1, 
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.05, delayChildren: 0.1 } 
  },
  exit: { 
    height: 0, 
    opacity: 0, 
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } 
  }
};

const mobileItemVars = {
  initial: { x: -10, opacity: 0 },
  animate: { x: 0, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
  exit: { x: -10, opacity: 0, transition: { duration: 0.2 } }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const location = useLocation();
const {user, setUser} = React.useContext(UserContext);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Close menus on route change
    setMobileOpen(false);
    setMegaOpen(false);
    
  }, [location.pathname]);

  // Real notifications, derived server-side from the student's own course
  // content (backend/get-notifications.php). Was a hardcoded demo array.
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [notifLoading, setNotifLoading]   = useState(false);

  useEffect(() => {
    if (!user?.session) { setNotifications([]); setUnreadCount(0); return; }
    let cancelled = false;

    const load = () => {
      setNotifLoading(true);
      axios
        .post(`${NOTIF_API}/get-notifications.php`, { session: user.session })
        .then((res) => {
          if (cancelled || !res.data?.success) return;
          setNotifications(res.data.data || []);
          setUnreadCount(res.data.count || 0);
        })
        .catch(() => { /* the bell just stays empty */ })
        .finally(() => { if (!cancelled) setNotifLoading(false); });
    };

    load();
    // Near-realtime without websockets, the same way the admin bell polls.
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.session]);

  // Opening the bell clears the badge; the rows stay styled as they were so the
  // student can still see which ones were new.
  const openNotifications = () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next && unreadCount > 0 && user?.session) {
      setUnreadCount(0);
      axios
        .post(`${NOTIF_API}/get-notifications.php?action=mark-read`, { session: user.session })
        .catch(() => { /* badge reappears on the next poll if this failed */ });
    }
  };

  const wishlistCount = getWishlist().length;

 

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Careers', path: '/careers' },
    { label: 'Blog', path: '/blog' },
    { label: 'For Colleges', path: '/for-colleges' },
    { label: 'Contact', path: '/contact' },
  ];
  const navigate = useNavigate();

  const logout = async () => {

  
    const data = new FormData();
    data.append("session", user.session);
    const response = await axios.post(
      "https://xceededge.com/logout.php", data, { headers: { "Content-Type": "multipart/form-data" } });
    console.log("Logout response:", response.data);
    if (response.data.status) {
      localStorage.removeItem('xceedEdge_user');
      setMobileOpen(false);
      setUser(null);

      navigate("/login");
      window.location.reload();

    } else {
      console.error("Logout failed:", response.data.message);
    
    }
    // navigate("/login");

  };
  console.log(user);

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${
          scrolled
            ? 'glass shadow-sm py-3 border-border/40' 
            : 'bg-transparent backdrop-blur-sm py-5 border-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">

          {/* Rectangular Logo */}
          <Link to="/" className="flex items-center shrink-0 z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-36 h-10 md:w-48 md:h-12 relative flex items-center overflow-hidden"
            >
              <img
                src="https://www.xceededge.com/data/images/header-logo2.png"
                alt="XceedEdge Logo"
                className="w-full h-full object-cover object-left"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors relative group ${location.pathname === '/' ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`}
            >
              Home
              {location.pathname === '/' && (
                <motion.span layoutId="activeNav" className="absolute -bottom-1.5 left-0 w-full h-[2px] bg-primary rounded-full" />
              )}
            </Link>

            {/* Mega Menu Toggle */}
            <div
              className="relative py-2"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button 
                className={`text-sm font-medium transition-colors flex items-center gap-1 ${location.pathname.includes('/courses') ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`}
                aria-expanded={megaOpen}
              >
                Courses
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${megaOpen ? 'rotate-180 text-primary' : ''}`} />
              </button>

              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 15, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[550px] bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-border/50 p-5 grid grid-cols-2 gap-3"
                  >
                    {CATEGORIES.map((cat, idx) => (
                      <motion.div 
                        key={cat.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Link
                          to={`/courses?category=${cat.id}`}
                          className="flex items-start gap-4 p-3 rounded-xl hover:bg-muted/60 transition-all duration-300 group hover:-translate-y-0.5"
                        >
                          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground shadow-sm transition-colors duration-300">
                            <span className="text-sm font-bold uppercase">
                              {cat.name[0]}
                            </span>
                          </div>
                          <div className="flex flex-col justify-center">
                            <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                              {cat.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {cat.count} curated courses
                            </p>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Standard Links */}
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors relative group ${location.pathname === link.path ? 'text-primary' : 'text-foreground/80 hover:text-primary'}`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.span layoutId="activeNav" className="absolute -bottom-1.5 left-0 w-full h-[2px] bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Interface Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSearchOpen(true)} 
              className="text-foreground/70 hover:text-primary hover:bg-primary/5 rounded-full"
              aria-label="Open Search"
            >
              <Search className="w-5 h-5" />
            </Button>

            {user? (
              <>
                <div className="relative hidden sm:block">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={openNotifications} 
                    className={`rounded-full transition-colors ${notifOpen ? 'bg-primary/10 text-primary' : 'text-foreground/70 hover:text-primary hover:bg-primary/5'}`}
                    aria-label="Notifications"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-destructive rounded-full border-2 border-background" />
                    )}
                  </Button>

                  <AnimatePresence>
                    {notifOpen && (
                      <NotificationDropdown
                        notifications={notifications}
                        loading={notifLoading}
                        onClose={() => setNotifOpen(false)}
                      />
                    )}
                  </AnimatePresence>
                </div>

              

                <div className="hidden sm:flex items-center gap-2 ml-2 pl-3 border-l border-border/60">
                  <Link to={user.role === 'admin' ? '/admin' : '/dashboard'}>
                    <Button variant="ghost" size="sm" className="gap-2 rounded-full font-medium hover:bg-primary/5">
                      <LayoutDashboard className="w-4 h-4" />
                      <span className="hidden md:inline">Dashboard</span>
                    </Button>
                  </Link>

                  <Link to="/profile">
                    <Button variant="ghost" size="icon" className="text-foreground/70 hover:text-primary hover:bg-primary/5 rounded-full" aria-label="My Profile" title="My Profile">
                      <User className="w-4 h-4" />
                    </Button>
                  </Link>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    className="text-foreground/70 hover:text-destructive hover:bg-destructive/10 rounded-full"
                    aria-label="Log out"
                    title="Log out"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-2 ml-2 pl-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="rounded-full font-medium hover:bg-primary/5">
                    Log In
                  </Button>
                </Link>
                <Link to="/auth?mode=signup">
                  <Button size="sm" className="rounded-full font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Hamburger Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden rounded-full text-foreground/80 hover:bg-muted ml-1"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={mobileOpen ? 'close' : 'open'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </motion.div>
              </AnimatePresence>
            </Button>
          </div>
        </div>

        {/* Animated Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              variants={mobileMenuVars}
              initial="initial"
              animate="animate"
              exit="exit"
              className="lg:hidden bg-white border-b border-border/40 overflow-hidden shadow-2xl"
            >
              <div className="px-6 py-6 space-y-5">

                {/* Navigation Links */}
                <div className="flex flex-col space-y-1">
                  {navLinks.map((link) => (
                    <motion.div key={link.path} variants={mobileItemVars}>
                      <Link
                        to={link.path}
                        className={`block px-4 py-3.5 rounded-xl font-medium text-base transition-colors ${location.pathname === link.path ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground/80 hover:bg-gray-50 hover:text-primary'}`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                  <motion.div variants={mobileItemVars}>
                    <Link
                      to="/courses"
                      className={`block px-4 py-3.5 rounded-xl font-medium text-base transition-colors ${location.pathname === '/courses' ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground/80 hover:bg-gray-50 hover:text-primary'}`}
                    >
                      All Courses
                    </Link>
                  </motion.div>
                </div>

                <motion.div variants={mobileItemVars} className="h-px bg-gray-100" />

                {/* Mobile User Actions */}
                {user ? (
                  <motion.div variants={mobileItemVars} className="space-y-4">
                    {/* User greeting */}
                    <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-2xl">
                      <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center text-primary flex-shrink-0">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Welcome back</p>
                        <p className="text-xs text-muted-foreground capitalize">{user.role} Account</p>
                      </div>
                    </div>

                    {/* Dashboard + Profile row */}
                    <div className="grid grid-cols-2 gap-3">
                      <Link to={user.role === 'admin' ? '/admin' : '/dashboard'} className="block">
                        <Button variant="outline" className="w-full justify-center gap-2 rounded-2xl h-14 text-sm font-semibold border-gray-200 bg-white hover:bg-gray-50">
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </Button>
                      </Link>
                      <Link to="/profile" className="block">
                        <Button variant="outline" className="w-full justify-center gap-2 rounded-2xl h-14 text-sm font-semibold border-gray-200 bg-white hover:bg-gray-50">
                          <User className="w-4 h-4" />
                          Profile
                        </Button>
                      </Link>
                    </div>

                    {/* Log Out full width */}
                    <Button
                      variant="ghost"
                      onClick={logout}
                      className="w-full justify-center gap-2 rounded-2xl h-12 text-sm font-semibold text-destructive hover:text-destructive hover:bg-red-50 border border-red-100"
                    >
                      <LogOut className="w-4 h-4" />
                      Log Out
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div variants={mobileItemVars} className="grid grid-cols-2 gap-3">
                    <Link to="/login">
                      <Button variant="outline" className="w-full rounded-2xl h-13 text-base font-semibold border-gray-200">
                        Log In
                      </Button>
                    </Link>
                    <Link to="/auth?mode=signup">
                      <Button className="w-full rounded-2xl h-13 text-base font-semibold shadow-md">
                        Sign Up
                      </Button>
                    </Link>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Global Search Overlay */}
      <AnimatePresence>
        {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
      </AnimatePresence>
    </>
  );
}